import React, { useEffect, useMemo, useRef, useState } from 'react'
import Autosuggest, { RenderInputComponentProps } from 'react-autosuggest'
import { useProductSuggestionsLazyQuery } from '@graphql/queries/__generated__/product-suggestion.generated'
import useDebounce from '../../../hooks/useDebounce'
import { useTranslation } from 'react-i18next'
import { ProductSuggestionFragment } from '@graphql/fragments/__generated__/ProductSuggestion.generated'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { FORM_ERRORS_MAPPING } from '../../../../mappings'
import { Button, Divider, Grow, Stack, useTheme } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import Avatar from '@mui/material/Avatar'
import SearchIcon from '@icons/SearchIcon'
import CloseIcon from '@mui/icons-material/Close'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Paper from '@mui/material/Paper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuList from '@mui/material/MenuList'
import MenuItem from '@mui/material/MenuItem'
import Popper from '@mui/material/Popper'
import { useMainCategoryListQuery } from '@graphql/queries/__generated__/category.generated'

interface ISearchFormData {
  q: string
  categoryName: string
  categorySlug: string
  categoryId: number
}

const getSuggestionValue = (suggestion: ProductSuggestionFragment) => suggestion.specific?.shortName || suggestion.text

const renderSuggestion = (suggestion: ProductSuggestionFragment) => (
  <div className='flex items-center'>
    <Avatar
      alt={suggestion.text}
      src={suggestion.specific?.product?.thumbnailUrl}
      variant='rounded'
      sx={{ width: '25px', height: '25px' }}
    />
    <span className='ml-10'>{suggestion.specific?.shortName || suggestion.text}</span>
  </div>
)

interface IProps {
  initialQuery: string

  onSubmitSearch(values: ISearchFormData): Promise<void> | void
}

const HeaderSearch: React.FC<IProps> = ({ onSubmitSearch, initialQuery }) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down('sm'))
  const [suggestions, setSuggestions] = useState([])

  const divRef = React.useRef<HTMLDivElement>(null)

  const [isOpenCategoryMenu, setIsOpenCategoryMenu] = useState(false)

  const skipAutoFocusSuggestionsRef = useRef(true)
  useEffect(() => {
    skipAutoFocusSuggestionsRef.current = false
  }, [])
  const shouldRenderSuggestions = (value: string) =>
    !skipAutoFocusSuggestionsRef.current && value.trim().length > 0

  const categoriesQuery = useMainCategoryListQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const [loadProductSuggestions] = useProductSuggestionsLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: data => setSuggestions(data.productSuggestions),
  })

  const schema = useMemo(
    () =>
      Yup.object().shape<ISearchFormData>({
        q: Yup.string()
          .min(2, FORM_ERRORS_MAPPING(t, 'tooShortWithValue', { min: 3 }))
          .required(FORM_ERRORS_MAPPING(t, 'valueIsRequired')),
        categoryName: Yup.string().nullable(),
        categorySlug: Yup.string().nullable(),
        categoryId: Yup.number().nullable(),
      }),
    [t, i18n.language]
  )
  const form = useFormik<ISearchFormData>({
    enableReinitialize: false,
    initialValues: {
      q: initialQuery,
      categoryName: '',
      categorySlug: '',
      categoryId: null,
    },
    validationSchema: schema,
    onSubmit: onSubmitSearch,
  })

  const onSuggestionsFetchRequested = useDebounce(({ value }) => {
    if (form.values.categoryId) {
      void loadProductSuggestions({
        variables: {
          q: value,
          category: `${form.values.categoryName}|${form.values.categoryId}`,
          size: smDown ? 4 : 6,
        },
      })
    } else {
      void loadProductSuggestions({ variables: { q: value, size: smDown ? 4 : 6 } })
    }
  }, 250)

  const onSuggestionsClearRequested = () => {
    setSuggestions([])
  }

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    return (
      <div
        {...containerProps}
        style={{
          top: '11px',
          borderRadius: '25px',
          paddingTop: '40px',
          borderBottomLeftRadius: '15px',
          borderBottomRightRadius: '15px',
        }}
      >
        {children}
        <div>
          <Divider />
          <Button
            variant='text'
            size='medium'
            className='transparent'
            onClick={() => {
              form.handleSubmit()
              setSuggestions([])
            }}
            sx={{ justifyContent: 'left', padding: '8px 30px' }}
            disableElevation
            disableFocusRipple
            disableRipple
            fullWidth
          >
            View all results
          </Button>
        </div>
      </div>
    )
  }
  const handleSelectCategory = (categoryId: number | null) => {
    if (categoryId) {
      const selectedCategory = categoriesQuery.data?.categories?.find(category => category.pk === categoryId)
      form.setFieldValue('categoryName', selectedCategory.name)
      form.setFieldValue('categoryId', categoryId)
      form.setFieldValue('categorySlug', selectedCategory.slug)
    } else {
      form.setFieldValue('categoryName', 'All categories')
      form.setFieldValue('categoryId', null)
    }
    setIsOpenCategoryMenu(false)
  }

  const selectedCategory = categoriesQuery.data?.categories?.find(category => category.pk === form.values.categoryId)

  const renderInputComponent = (inputProps: RenderInputComponentProps) => (
    <div className='flex items-center justify-content-center w-full h-full relative z-5' ref={divRef}>
      <div>
        <div className='absolute' style={{ top: '12px', borderRight: '1px solid #eee' }}>
          <Button
            variant='text'
            className='transparent'
            endIcon={isOpenCategoryMenu ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            size='medium'
            onClick={() => setIsOpenCategoryMenu(true)}
            disableElevation
            disableFocusRipple
            disableRipple
          >
            <span
              className='text-capitalize'
              style={{
                width: '150px',
                maxWidth: '150px',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                fontSize: '14px',
              }}
            >
              {selectedCategory ? selectedCategory.name : 'All categories'}
            </span>
          </Button>
          <Popper
            open={isOpenCategoryMenu}
            anchorEl={divRef.current}
            style={{
              width: 207,
              borderRadius: '30px',
              borderBottomLeftRadius: '15px',
              borderBottomRightRadius: '15px',
              overflow: 'hidden',
              zIndex: -1,
              border: '1px solid #eee',
            }}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, -52],
                },
              },
            ]}
            role={undefined}
            placement='bottom-start'
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                timeout={0}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}
              >
                <Paper sx={{ paddingTop: '50px' }}>
                  <ClickAwayListener onClickAway={() => setIsOpenCategoryMenu(false)}>
                    <MenuList autoFocusItem={isOpenCategoryMenu}>
                      <MenuItem key='all' onClick={() => handleSelectCategory(null)}>
                        All categories
                      </MenuItem>
                      {categoriesQuery.data?.categories?.map(category => {
                        return (
                          <MenuItem key={category.id} onClick={() => handleSelectCategory(category.pk)}>
                            {category.name}
                          </MenuItem>
                        )
                      })}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
      </div>

      <input {...inputProps} />
      <Stack
        direction='row'
        divider={<Divider orientation='vertical' flexItem />}
        spacing={1}
        sx={{ position: 'absolute', right: '20px' }}
        className='cursor-pointer'
      >
        {!!form.values.q && <CloseIcon onClick={() => form.setFieldValue('q', '')} />}
        <SearchIcon onClick={() => form.handleSubmit()} />
      </Stack>
    </div>
  )
  const onSuggestionSelected = () => {
    form.handleSubmit()
  }

  const inputProps = {
    placeholder: t('placeholder.searchProductsByNames', 'Search products by names, needs, categories...'),
    value: form.values.q,
    onChange: (event: React.FormEvent<any>, { newValue }) => form.setFieldValue('q', newValue),
    onBlur: form.handleBlur,
    name: 'q',
    autoFocus: true,
    className: 'not-mini data-hj-allow flex-1 border-0',
    id: 'searchInput',
    'aria-label': 'q',
    style: {
      padding: '14px 80px 14px 220px',
      fontSize: '16px',
      border: '1px solid #ccc',
      borderRadius: '50px',
    },
  }

  return (
    <form className='h-full' onSubmit={form.handleSubmit}>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
        renderInputComponent={renderInputComponent}
        onSuggestionSelected={onSuggestionSelected}
        renderSuggestionsContainer={renderSuggestionsContainer}
        shouldRenderSuggestions={shouldRenderSuggestions}
      />
    </form>
  )
}

export default HeaderSearch
